import ConfirmModal from 'components/common/ConfirmModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import MenuDetailModal from 'features/MenuDetail/pages/MenuDetail';
import SubMenuDetailModal from 'features/MenuDetail/pages/SubMenuDetail';
import { Menu } from 'features/MenuList/models/menu';
import { useEffect, useRef, useState } from 'react';
import { Col, Form, ModalProps, Row } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TfiReload } from 'react-icons/tfi';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { MENU_TYPE } from 'utils';
import SubTitleDetailModal from '../../../../SubTitleMenu/pages/detail/Detail';
import service from '../services';

const COUPON_CLASSIFICATION_KEY: any = {
  '0': 'U',
  '1': 'C',
  '2': 'T',
};

// define props
interface DataTableProps {
  dataTable: Menu[];
  setDataTable: (dataTable: Menu[]) => void;
  handleBtnOnReflect: (selectedMenus: Array<{ id: number; name: string; coupon_classification: number }>) => void;
  except_menu_ids?: number[];
  menuClassification: string;
  name: string;
  menuCode: string;
  dataDate: Date;
}

function TableList({
  dataTable,
  setDataTable,
  handleBtnOnReflect,
  except_menu_ids,
  menuClassification,
  name,
  menuCode,
  dataDate,
}: DataTableProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const initialState = dataTable.reduce<{ [key: number]: boolean }>((object, menu) => {
    object[menu.id] = false;
    return object;
  }, {});
  const [checked, setChecked] = useState<any>(initialState);

  // ref
  const refBtnOnReflect = useRef<HTMLButtonElement>(null);
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  const subMenuDetailModalRef = useRef<ModalProps>(SubMenuDetailModal);

  // Subtitle Menu Modal
  const [showSubTitleMenuDetailModal, setShowSubTitleMenuDetailModal] = useState(false);
  const [subTitleMenuDetailModalData, setSubTitleMenuDetailModalData] = useState<{ id: number }>();

  // main_menu detail modal events
  const handleOpenMainMenuModal = (menuId: number) => {
    menuDetailModalRef.current.openModalExternal(menuId);
  };

  function handleSubMenuModalClose() {
    subMenuDetailModalRef.current.onReset();
  }

  // open subtitle menu modal events
  function handleOpenSubTitleMenuDetailModal(subTitleMenuId: number) {
    setShowSubTitleMenuDetailModal(true);
    setSubTitleMenuDetailModalData({ id: subTitleMenuId });
  }

  function handleCloseSubTitleMenuDetailModal(status: boolean) {
    if (status) {
      handleReload();
    }
    setShowSubTitleMenuDetailModal(false);
    setSubTitleMenuDetailModalData(undefined);
  }

  // handle checked all
  function handleCheckedAll() {
    const newState = dataTable.reduce<{ [key: number]: boolean }>((object, menu) => {
      object[menu.id] = true;
      return object;
    }, {});
    setChecked(newState);
  }

  // handle unchecked all state changes
  function handleUncheckedAll() {
    const newState = dataTable.reduce<{ [key: number]: boolean }>((object, menu) => {
      object[menu.id] = false;
      return object;
    }, {});
    setChecked(newState);
  }

  // toggle checkbox
  function toggleCheck(value: number) {
    let newState = { ...checked };
    newState[value] = !newState[value];
    setChecked(newState);
  }

  // handle reload
  async function handleReload() {
    await service.getMenus('', '', '', dataDate).then((result) => {
      setDataTable(result.data);
    });
    handleUncheckedAll();
  }

  // useEffects check change checked
  useEffect(() => {
    // handle disabled btn view detail
    function handleDisableBtnViewDetail() {
      let count = Object.keys(checked).filter((key) => checked[key] === true).length;
      if (count > 0) {
        return false;
      }
      return true;
    }

    if (handleDisableBtnViewDetail()) {
      if (refBtnOnReflect.current) refBtnOnReflect.current.disabled = true;
    } else {
      if (refBtnOnReflect.current) refBtnOnReflect.current.disabled = false;
    }
  }, [checked]);

  function handlOnReflect() {
    let ids = Object.keys(checked)
      .filter((key) => checked[key] === true)
      .map((value) => parseInt(value));

    const processOnReflect = (menuItems: Menu[]) => {
      const selectedMenus: Array<{ id: number; name: string; coupon_classification: number }> = menuItems.map(
        (dataItem) => {
          const menuName = dataItem.food_system_menu.name;
          return {
            id: dataItem.id,
            name: menuName,
            coupon_classification: dataItem.coupon_classification,
          };
        }
      );
      handleBtnOnReflect(selectedMenus);
    };

    service.checkMenuSub(ids, dataDate).then((result) => {
      const subMenuItems = result.data.filter((menu: Menu) => !menu.accepted_menu);
      if (subMenuItems.length > 0) {
        const menuItems = result.data.filter((menu: Menu) => menu.accepted_menu);
        showGlobalModal(() => (
          <ConfirmModal
            title={t('App.confirm')}
            message={t('MenuAddModal.confirm')}
            onConfirm={() => processOnReflect(menuItems)}
          />
        ));
      } else {
        const menuItems = result.data.filter((menu: any) => menu.accepted_menu);
        processOnReflect(menuItems);
      }
    });
  }
  const handleMenuModalClose = () => {
    menuDetailModalRef.current.onReset();
  };

  function handleOpenMenuModal(menu: Menu) {
    if (menu.type === MENU_TYPE.DEFAULT) {
      handleOpenMainMenuModal(menu.id);
    } else {
      handleOpenSubTitleMenuDetailModal(menu.id);
    }
  }

  const filteredDataTable = dataTable.filter((menu) => !except_menu_ids?.includes(menu.id));

  return (
    <>
      <Row className="mb-2">
        <Col sm={6} lg={6}>
          <button className="link" onClick={handleCheckedAll}>
            {t('Genre.Button.select_all')}
          </button>{' '}
          /{' '}
          <button className="link" onClick={handleUncheckedAll}>
            {t('Genre.Button.cancel_all')}
          </button>
          <span className="ps-2 pe-2"></span>
          <button type="button" onClick={handleReload} className="btn-primary btn-circle">
            <TfiReload />
          </button>
          {t('Genre.Button.reload')}
        </Col>
        <Col sm={6} lg={6} className="d-flex justify-content-end">
          <span className="ps-2 pe-2"></span>
          <button type="button" className="btn btn-success" ref={refBtnOnReflect} onClick={handlOnReflect}>
            {t('Genre.Button.add_new')}
          </button>
        </Col>
      </Row>

      <div className="d-flex flex-column" style={{ height: '352px', backgroundColor: '#999' }}>
        <div className="dan-table">
          <div className="dan-thead">
            <div className="dan-tr">
              <div className="dan-th" style={{ width: '10%' }}>
                {t('Genre.Table.selection')}
              </div>
              <div className="dan-th border-start-0" style={{ width: '30%' }}>
                メニューコード
              </div>
              <div className="dan-th border-start-0" style={{ width: '60%' }}>
                メニュー名
              </div>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 flex-shrink-1">
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                className="fixed-size-list"
                width={width}
                height={height}
                itemSize={32}
                itemCount={filteredDataTable.length}
                itemData={filteredDataTable}
              >
                {({ style, index }) => {
                  const menu = dataTable[index];

                  return (
                    <div key={index} id={`row-${index}`} className={`dan-tr d-flex`} style={style}>
                      <div className="dan-td d-flex justify-content-center" style={{ width: '10%' }}>
                        <Form.Check
                          type="checkbox"
                          className="custom-checkbox"
                          value={menu.id}
                          checked={checked[menu.id]}
                          onChange={() => toggleCheck(menu.id)}
                          // disabled={except_menu_ids?.includes(menu.id)}
                        />
                      </div>
                      <div className="dan-td d-flex justify-content-center" style={{ width: '30%' }}>
                        {menu.food_system_menu.cd}
                        {COUPON_CLASSIFICATION_KEY[menu.coupon_classification]
                          ? COUPON_CLASSIFICATION_KEY[menu.coupon_classification]
                          : ''}
                      </div>
                      <div className="dan-td d-flex justify-content-center" style={{ width: '60%' }}>
                        <button
                          className="pull-right html-link"
                          onClick={handleOpenMenuModal.bind(null, menu)}
                          data-id={menu.id}
                          style={{ cursor: 'pointer' }}
                        >
                          {menu.food_system_menu.name}
                        </button>
                      </div>
                    </div>
                  );
                }}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      </div>

      <MenuDetailModal
        ref={menuDetailModalRef}
        // menuCallBack={handleMenuCallback}
        onMenuModalClose={handleMenuModalClose}
      />

      <SubMenuDetailModal
        ref={subMenuDetailModalRef}
        onMenuModalClose={handleSubMenuModalClose}
        // menuCallBack={handleSubMenuModalSaveSuccess}
      />

      {showSubTitleMenuDetailModal &&
        subTitleMenuDetailModalData &&
        createPortal(
          <SubTitleDetailModal
            id={subTitleMenuDetailModalData?.id.toString() || ''}
            show={true}
            onClose={handleCloseSubTitleMenuDetailModal}
          />,
          document.body
        )}
    </>
  );
}

export default TableList;
